import { EMPLOYEE_CATEGORIES, HEADCOUNT_TITLE_IDS } from '../constants/trainingConstants';

/**
 * Turns the raw `titleId -> value` map returned by getQuantitativeHeadcount into
 * per-category headcount totals ({ male, female, total }) keyed the same way as
 * HEADCOUNT_TITLE_IDS, ready to be rendered as the "out of" denominator.
 *
 * @param {Object} quantMap - Map of reporting-question-title id to numeric total.
 * @returns {Object} Map of category key to { male, female, total }.
 */
export const buildHeadcountByCategory = (quantMap = {}) => {
  return Object.entries(HEADCOUNT_TITLE_IDS).reduce((acc, [category, { male, female }]) => {
    const maleCount = Number(quantMap?.[male]) || 0;
    const femaleCount = Number(quantMap?.[female]) || 0;
    acc[category] = { male: maleCount, female: femaleCount, total: maleCount + femaleCount };
    return acc;
  }, {});
};

/**
 * Sums the `total` headcount across the given category keys of a
 * buildHeadcountByCategory map — used to build the summary-card denominators.
 *
 * @param {Object} headcounts - Map of category key to { male, female, total }.
 * @param {string[]} categories - Category keys to sum.
 * @returns {number} Summed total (0 when none are available).
 */
export const sumHeadcountTotals = (headcounts = {}, categories = []) =>
  categories.reduce((sum, key) => sum + (Number(headcounts?.[key]?.total) || 0), 0);

/**
 * Per-segment company headcount for the principle-coverage tables, derived from the
 * optimized-analytics `categoryCoverage` map, one entry per row/column: BoD, KMP,
 * permanent employees (Employees-other = permanentEmployee minus KMP, since
 * permanentEmployee already includes KMP, which has its own row), other than
 * permanent employees, permanent workers, and other than permanent workers. Keyed by
 * the EMPLOYEE_CATEGORIES labels.
 *
 * @param {Object} categoryCoverage - analytics.categoryCoverage map.
 * @returns {Object} Segment label -> total headcount (0 when unavailable).
 */
export const segmentHeadcountsFromCoverage = (categoryCoverage) => {
  const total = (key) => categoryCoverage?.[key]?.headcount?.total ?? 0;
  return {
    [EMPLOYEE_CATEGORIES.BOARD_OF_DIRECTORS]: total('boardOfDirectors'),
    [EMPLOYEE_CATEGORIES.KEY_MANAGERIAL_PERSONNEL]: total('keyManagerialPersonnel'),
    [EMPLOYEE_CATEGORIES.EMPLOYEES_OTHER]: Math.max(
      0,
      total('permanentEmployee') - total('keyManagerialPersonnel')
    ),
    [EMPLOYEE_CATEGORIES.OTHER_THAN_PERMANENT_EMPLOYEES]: total('otherThanPermanentEmployee'),
    [EMPLOYEE_CATEGORIES.WORKERS]: total('permanentWorker'),
    [EMPLOYEE_CATEGORIES.OTHER_THAN_PERMANENT_WORKERS]: total('otherThanPermanentWorker')
  };
};

/**
 * Employee (including BoD and KMPs) and worker gender-split headcount totals from
 * the optimized-analytics `categoryCoverage` map — the "Total (A)" split used as
 * coverage denominators. Permanent + Other-than-Permanent Employee already include
 * BoD/KMP, so they are summed as-is (not added again).
 *
 * @param {Object} categoryCoverage - analytics.categoryCoverage map.
 * @returns {Object} { employees: {male,female,total}, workers: {male,female,total} }.
 */
export const workforceHeadcountFromCoverage = (categoryCoverage) => {
  const headcount = (key) => categoryCoverage?.[key]?.headcount || { male: 0, female: 0, total: 0 };
  const sum = (keys, field) => keys.reduce((acc, key) => acc + (Number(headcount(key)[field]) || 0), 0);
  const employeeKeys = ['permanentEmployee', 'otherThanPermanentEmployee'];
  const workerKeys = ['permanentWorker', 'otherThanPermanentWorker'];
  return {
    employees: { male: sum(employeeKeys, 'male'), female: sum(employeeKeys, 'female'), total: sum(employeeKeys, 'total') },
    workers: { male: sum(workerKeys, 'male'), female: sum(workerKeys, 'female'), total: sum(workerKeys, 'total') }
  };
};

// Helper function to categorize employees
export const categorizeEmployee = (user) => {
  const category = user.categoryId?.toLowerCase();

  // You may need to adjust these mappings based on your actual data
  if (category?.includes('bod') || category?.includes('director')) {
    return EMPLOYEE_CATEGORIES.BOARD_OF_DIRECTORS;
  } else if (category?.includes('kmp') || category?.includes('kmp')) {
    return EMPLOYEE_CATEGORIES.KEY_MANAGERIAL_PERSONNEL;
  } else if (category?.includes('permanent employee') || category?.includes('other than permanent employee')) {
    return EMPLOYEE_CATEGORIES.EMPLOYEES_OTHER;
  } else if (category?.includes('permanent worker') || category?.includes('other than permanent worker')) {
    return EMPLOYEE_CATEGORIES.WORKERS;
  } else {
    // Default categorization - you might want to adjust this
    return EMPLOYEE_CATEGORIES.EMPLOYEES_OTHER;
  }
};

/**
 * Segment classification for the principle-coverage tables, one segment per row/
 * column: BoD, KMP, permanent employees ("Employees other than BoD and KMPs"), other
 * than permanent employees, permanent workers ("Workers") and other than permanent
 * workers. Any unrecognised category returns null and is dropped.
 *
 * `categoryId` is a free-form label ("Other than Permanent Employee", "Permanent
 * Worker", "KMP", …) matched by substring so wording variants are tolerated. The
 * naive if-chain is order-sensitive — "other than permanent employee" contains
 * "permanent employee", so the specific check must precede the general one or every
 * other-than-permanent person misclassifies as permanent. Instead the workforce and
 * permanence axes are derived as independent flags and combined, so the classification
 * no longer depends on branch order.
 *
 * @param {Object} user - Trainee/attendee carrying a `categoryId` label.
 * @returns {string|null} An EMPLOYEE_CATEGORIES segment label, or null when unmatched.
 */
export const principleSegmentOf = (user) => {
  const category = user.categoryId?.toLowerCase();
  if (!category) return null;
  if (category.includes('bod') || category.includes('director')) {
    return EMPLOYEE_CATEGORIES.BOARD_OF_DIRECTORS;
  }
  if (category.includes('kmp')) {
    return EMPLOYEE_CATEGORIES.KEY_MANAGERIAL_PERSONNEL;
  }
  const isWorker = category.includes('worker');
  const isEmployee = category.includes('employee');
  if (!isWorker && !isEmployee) return null;
  const isOtherThanPermanent =
    category.includes('other than permanent') || category.includes('temporary');
  if (isWorker) {
    return isOtherThanPermanent
      ? EMPLOYEE_CATEGORIES.OTHER_THAN_PERMANENT_WORKERS
      : EMPLOYEE_CATEGORIES.WORKERS;
  }
  return isOtherThanPermanent
    ? EMPLOYEE_CATEGORIES.OTHER_THAN_PERMANENT_EMPLOYEES
    : EMPLOYEE_CATEGORIES.EMPLOYEES_OTHER;
};

// Helper function to calculate training hours
export const calculateTrainingHours = (fromTime, toTime) => {
  if (!fromTime || !toTime) return 0;
  
  const [fromHour, fromMin] = fromTime.split(':').map(Number);
  const [toHour, toMin] = toTime.split(':').map(Number);
  
  const fromTotalMin = fromHour * 60 + fromMin;
  const toTotalMin = toHour * 60 + toMin;
  
  return Math.max(0, (toTotalMin - fromTotalMin) / 60);
};

// Helper function to get current financial year label
export const getCurrentFinancialYearLabel = (financialYear, financialYearId) => {
  const currentFY = financialYear.find(fy => fy.id === financialYearId);
  if (currentFY) {
    const fromDate = new Date(currentFY.fromDate);
    const toDate = new Date(currentFY.toDate);
    
    if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
      const fromYear = fromDate.getFullYear();
      const toYear = toDate.getFullYear();
      return `FY ${fromYear}-${toYear.toString().slice(-2)}`;
    }
  }
  return 'Current FY';
};

// Helper function to format percentage
export const formatPercentage = (count, total) => {
  if (!total || total === 0) return '0%';
  return `${((count / total) * 100).toFixed(2)}%`;
};

// Helper function for safe property access
export const safeGet = (obj, path, defaultValue = 0) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};